import _classNames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import ComplexIcon from '../icons/ComplexIcon/ComplexIcon';
import Tooltip from '../tooltip/tooltip';

/**
 * A functional component that renders an item in the `Sidebar` component
 */
function SidebarItem({
  active,
  text,
  subtext,
  className,
  hidden,
  href,
  onClick,
  iconName,
  showtooltip,
  disabled,
}) {
  if (hidden) return null;
  const classes = _classNames('Sidebar__item', className, {
    'Sidebar__item--active': active && !disabled,
    'Sidebar__item--disabled': disabled,
  });

  const item = (
    <a className={classes} href={!disabled && href} onClick={!disabled && onClick}>
      {iconName && (
        <div className="Sidebar__item__icon-container">
          {typeof iconName === 'string' ? (
            <ComplexIcon name={iconName} classes="Sidebar__item__icon" />
          ) : (
            iconName
          )}
        </div>
      )}
      <span className="Sidebar__item__text">
        {text}
        {subtext && <span className="Sidebar__item__subtext">{subtext}</span>}
      </span>
    </a>
  );

  return showtooltip ? (
    <Tooltip
      block
      position="right"
      hoverOpenDelay={1000}
      content={text}
      className="Sidebar__item__tooltip"
    >
      {item}
    </Tooltip>
  ) : (
    item
  );
}

SidebarItem.propTypes = {
  /**
   * Render item in active state
   */
  active: PropTypes.bool,
  /**
   * The item text (i.e. "Home")
   */
  text: PropTypes.string,
  /**
   * The subtext (i.e. "BETA"). Optional.
   */
  subtext: PropTypes.string,
  /**
   * A space-separated list of classes to apply to the outer link tag of the item component
   */
  className: PropTypes.string,
  /**
   * If the item is not in a disabled state, the `href` value is passed to the outer link tag of the
   * item component
   */
  href: PropTypes.string,
  /**
   * If the item is not in a disabled state, the `onClick` function is passed to the outer link tag
   * of the item component
   */
  onClick: PropTypes.func,
  /**
   * When set to `true`, the component returns `null` (hidden state)
   */
  hidden: PropTypes.bool,
  /**
   * Name of a `ComplexIcon` to include on the left side of the item
   */
  iconName: PropTypes.string,
  /**
   * Set to `true` to render a `Tooltip` wrapping the item. The tooltip text will be the same as the
   * value of the `text` prop.
   */
  showtooltip: PropTypes.bool,
  /**
   * The item disabled state shows the item in the list in a darkened color and cursor `not-allowed`
   */
  disabled: PropTypes.bool,
};

SidebarItem.defaultProps = {
  active: false,
  text: '',
  subtext: '',
  onClick: () => {},
  hidden: false,
  showtooltip: false,
  disabled: false,
  className: undefined,
  href: undefined,
  iconName: undefined,
};

export default SidebarItem;
